import { useEffect, useState } from 'react';
import { Plus, Trash2, Sparkles, History, AlertTriangle } from 'lucide-react';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectSeparator, SelectTrigger, SelectValue } from '../ui/select';
import { ProjectPicker } from './ProjectPicker';
import { BulkLineBar, UndoBar } from './BulkLineBar';
import { CATEGORY_LABELS, CATEGORY_ORDER } from './lineCategories';

export { CATEGORY_LABELS, CATEGORY_ORDER };
export const fmtGBP = (pence) => ((typeof pence === 'number' ? pence : 0) / 100).toLocaleString('en-GB', { style: 'currency', currency: 'GBP' });
export const toPence = (v) => { const n = parseFloat(String(v ?? '').replace(/[£,\s]/g, '')); return Number.isFinite(n) ? Math.round(n * 100) : 0; };
export const newLine = (over = {}) => ({ id: `tmp-${Math.random().toString(36).slice(2, 10)}`, project_id: '', no_project: false, category: '', description: '', amount: '', vat_rate: null, suggested: false, ...over });

const cell = 'bg-[#1E2530] border-white/20 text-white h-9 text-sm';
const GRID = 'grid grid-cols-[22px_minmax(0,1.6fr)_104px_minmax(0,1fr)_104px_62px_26px] gap-1.5';
const VAT_NONE = 'none';

function VatRateSelect({ value, onChange, disabled, testid, memory = false }) {
  const none = value == null || value === '';
  return (
    <Select value={none ? VAT_NONE : String(Number(value))} onValueChange={v => onChange(v === VAT_NONE ? null : Number(v))} disabled={disabled}>
      <SelectTrigger className={`${cell} px-2 ${memory ? 'border-sky-400/60 text-sky-200' : ''}`}
        title={memory ? "Rate from this supplier's past invoices — check it against the invoice" : 'VAT rate on this line — leave as pro-rata unless the invoice rates lines differently'}
        data-testid={testid} data-memory={memory ? 'true' : undefined}>
        <SelectValue>{none ? <span className="text-gray-500">—</span> : `${Number(value)}%`}</SelectValue>
      </SelectTrigger>
      <SelectContent className="bg-[#2A3240] border-white/10">
        <SelectItem value={VAT_NONE} className="text-white text-sm">— pro-rata</SelectItem>
        {['20', '5', '0'].map(r => <SelectItem key={r} value={r} className="text-white text-sm">{r}%</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

export const SEP = ' — ';
export const shortRef = (ref) => (ref || '').split('-')[0].trim() || (ref || '');
/** Put `prefix` in front of a description (skipped when it is already there). Never touches anything else. */
export const withPrefix = (description, prefix) => {
  const p = (prefix || '').trim();
  const cur = (description || '').trim();
  if (!p || cur.toLowerCase().startsWith(p.toLowerCase())) return description || '';
  return cur ? `${p}${SEP}${cur}` : p;
};

/**
 * Split an invoice across projects / categories. One ledger row is posted
 * per line. Suggested (AI) lines carry a sparkle until the user edits them.
 * Tick lines to set their Project / Category in one go (or delete them); one Undo restores the whole batch.
 */
export function LineItemsTable({ lines, setLines, projects, invoiceTotalPence, grossPence = 0, vatPence = 0, aiVatPence = null, readOnly = false, linesVersion = 0, aiDiffers = false, onUseAiTotals, company = null, api, onAdhocCreated, cis = false }) {
  const [selected, setSelected] = useState(() => new Set());
  const [undo, setUndo] = useState(null);
  useEffect(() => { setSelected(new Set()); setUndo(null); }, [linesVersion]);
  // CIS invoices are Labour / Materials / Fuel only (the deduction is worked out from the split).
  const CATS = cis ? CATEGORY_ORDER.filter(c => c !== 'other') : CATEGORY_ORDER;
  const offCis = (l) => cis && l.category && !CATS.includes(l.category);

  const linesTotal = lines.reduce((s, l) => s + toPence(l.amount), 0);
  const diff = (invoiceTotalPence || 0) - linesTotal;
  const balanced = lines.length > 0 && diff === 0 && (invoiceTotalPence || 0) > 0;
  const unassigned = lines.filter(l => !l.project_id && !l.no_project).length;
  // Lines that add up to the gross were keyed inc VAT — offer to strip the VAT pro-rata
  const linesLookGross = lines.length > 0 && grossPence > 0 && grossPence !== (invoiceTotalPence || 0) && linesTotal === grossPence;
  // Unallocated equal to the VAT to the penny (the panel's or the reader's) = the Net is really the gross. Never spread VAT into ex-VAT lines.
  const gapIsVat = diff > 0 && ((vatPence > 0 && diff === vatPence) || (aiVatPence > 0 && diff === aiVatPence));

  const update = (id, patch) => setLines(ls => ls.map(l => l.id === id ? { ...l, ...patch, suggested: false } : l));
  const remove = (id) => setLines(ls => ls.filter(l => l.id !== id));
  const add = () => setLines(ls => [...ls, newLine({ amount: diff > 0 ? (diff / 100).toFixed(2) : '', ...(cis ? { vat_rate: '0', vat_source: 'cis' } : {}) })]);
  const canSpread = !readOnly && lines.length > 0 && diff !== 0 && linesTotal > 0 && (invoiceTotalPence || 0) > 0 && !gapIsVat;
  const spread = () => setLines(ls => {
    const cur = ls.reduce((s, l) => s + toPence(l.amount), 0);
    let acc = 0;
    return ls.map((l, i) => {
      const p = i === ls.length - 1 ? invoiceTotalPence - acc : Math.round(toPence(l.amount) * invoiceTotalPence / cur);
      acc += p;
      return { ...l, amount: (p / 100).toFixed(2), suggested: false };
    });
  });

  // ── bulk: only project / category / description-prefix ever change; amounts + VAT are never touched ──
  const bulk = (ids, patch, label) => {
    const patchFor = typeof patch === 'function' ? patch : () => patch;
    setUndo({ label, lines });
    setLines(ls => ls.map(l => (ids.has(l.id) ? { ...l, ...patchFor(l), suggested: false } : l)));
    setSelected(new Set());
  };
  const applySelected = ({ project_id, category }) => {
    const patch = {}; const what = [];
    if (project_id) { patch.project_id = project_id; what.push(`job ${projects.find(p => p.id === project_id)?.project_ref || ''}`); }
    if (category) { patch.category = category; what.push(CATEGORY_LABELS[category]); }
    bulk(selected, patch, `Set ${what.join(' + ')} on ${selected.size} line${selected.size === 1 ? '' : 's'}`);
  };
  const prefixSelected = (text) => bulk(selected, (l) => ({ description: withPrefix(l.description, text) }), `Prefixed ${selected.size} line${selected.size === 1 ? '' : 's'} with “${text.trim()}”`);
  const prefixJobRef = () => {
    const ids = new Set(lines.filter(l => selected.has(l.id) && l.project_id).map(l => l.id));
    bulk(ids, (l) => ({ description: withPrefix(l.description, shortRef(projects.find(p => p.id === l.project_id)?.project_ref)) }), `Prefixed ${ids.size} line${ids.size === 1 ? '' : 's'} with their job ref`);
  };
  const selectedWithJob = lines.filter(l => selected.has(l.id) && l.project_id).length;
  const applyAll = (patch, what) => bulk(new Set(lines.map(l => l.id)), patch, `Set ${what} on all ${lines.length} lines`);
  const deleteSelected = () => {
    const n = selected.size;
    setUndo({ label: `Deleted ${n} line${n === 1 ? '' : 's'}`, lines });
    setLines(ls => ls.filter(l => !selected.has(l.id)));
    setSelected(new Set());
  };
  const doUndo = () => { setLines(() => undo.lines); setUndo(null); setSelected(new Set()); };
  const toggle = (id) => setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allSelected = lines.length > 0 && selected.size === lines.length;
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(lines.map(l => l.id)));
  const onCategory = (l, v) => (v.startsWith('all:') ? applyAll({ category: v.slice(4) }, CATEGORY_LABELS[v.slice(4)]) : update(l.id, { category: v }));

  return (
    <div data-testid="inbox-lines">
      <div className="flex items-center justify-between mb-1.5 gap-2 flex-wrap">
        <label className="text-xs text-gray-400">Line items · <span className="text-gray-300">ex VAT</span> · one ledger row per line</label>
        <div className="flex items-center gap-3">
          {unassigned > 0 && !readOnly && (
            <span className="text-[11px] text-amber-300" data-testid="inbox-unassigned-hint">
              {unassigned} line{unassigned === 1 ? '' : 's'} need{unassigned === 1 ? 's' : ''} a job — the reader only fills jobs it's sure of
            </span>
          )}
          {!readOnly && (
            <button type="button" onClick={add} className="inline-flex items-center gap-1 text-xs text-[#F5A623] hover:underline" data-testid="inbox-add-line">
              <Plus className="w-3.5 h-3.5" /> Add line
            </button>
          )}
        </div>
      </div>
      <div className="rounded-lg border border-white/10 overflow-hidden">
        {!readOnly && <UndoBar undo={undo} onUndo={doUndo} />}
        {!readOnly && selected.size > 0 && <BulkLineBar count={selected.size} projects={projects} onApply={applySelected} onDelete={deleteSelected} onClear={() => setSelected(new Set())} onPrefix={prefixSelected} onPrefixJobRef={prefixJobRef} withJob={selectedWithJob} />}
        <div className={`${GRID} px-2 py-1.5 text-[10px] uppercase tracking-wider text-gray-500 bg-white/[0.03] items-center`}>
          <span className="flex items-center">
            {!readOnly && lines.length > 0 && (
              <input type="checkbox" checked={allSelected} ref={el => { if (el) el.indeterminate = selected.size > 0 && !allSelected; }} onChange={toggleAll}
                className="accent-[#F5A623] w-3.5 h-3.5 cursor-pointer" title="Select all lines" data-testid="inbox-lines-select-all" />
            )}
          </span>
          <span>Project</span><span>Category</span><span>Description</span><span className="text-right">Amount £ <span className="normal-case tracking-normal text-gray-600">ex VAT</span></span><span>VAT</span><span />
        </div>
        {lines.length === 0 && (
          <div className="px-3 py-4 text-sm text-gray-500 text-center" data-testid="inbox-lines-empty">No lines yet — add one, or wait for the AI read.</div>
        )}
        {lines.map((l, idx) => (
          <div key={l.id}
            className={`${GRID} items-center px-2 py-1.5 border-t border-white/5 ${selected.has(l.id) ? 'bg-[#F5A623]/[0.08]' : l.suggested ? 'bg-amber-500/[0.06]' : ''}`}
            data-testid={`inbox-line-${idx}`}>
            <span className="flex items-center">
              {!readOnly && <input type="checkbox" checked={selected.has(l.id)} onChange={() => toggle(l.id)} className="accent-[#F5A623] w-3.5 h-3.5 cursor-pointer" data-testid={`inbox-line-select-${idx}`} />}
            </span>
            <ProjectPicker value={l.project_id || ''} onChange={v => update(l.id, { project_id: v, no_project: false })} projects={projects}
              onApplyAll={readOnly ? undefined : (pid) => applyAll({ project_id: pid, no_project: false }, `job ${projects.find(p => p.id === pid)?.project_ref || ''}`)}
              company={company} noProject={!!l.no_project}
              onNoProject={readOnly ? undefined : () => update(l.id, { project_id: '', no_project: true })}
              api={readOnly ? undefined : api} onAdhocCreated={readOnly ? undefined : onAdhocCreated}
              disabled={readOnly} invalid={!l.project_id && !l.no_project} testid={`inbox-line-project-${idx}`} />
            <Select value={l.category || ''} onValueChange={v => onCategory(l, v)} disabled={readOnly}>
              <SelectTrigger className={`${cell} ${!l.category || offCis(l) ? 'border-amber-500/50' : ''}`} title={offCis(l) ? 'CIS invoices use Labour, Materials or Fuel' : ''} data-testid={`inbox-line-category-${idx}`}>
                <SelectValue placeholder="Type…" />
              </SelectTrigger>
              <SelectContent className="bg-[#2A3240] border-white/10">
                {CATS.map(c => <SelectItem key={c} value={c} className="text-white text-sm">{CATEGORY_LABELS[c]}</SelectItem>)}
                {l.category === 'plant' && <SelectItem value="plant" className="text-white text-sm">Plant</SelectItem>}
                {cis && l.category === 'other' && <SelectItem value="other" className="text-amber-300 text-sm">Other · not CIS</SelectItem>}
                {!readOnly && lines.length > 1 && (
                  <SelectGroup>
                    <SelectSeparator className="bg-white/10" />
                    <SelectLabel className="text-[10px] uppercase tracking-wider text-gray-500">Apply to all lines</SelectLabel>
                    {CATS.map(c => <SelectItem key={`all:${c}`} value={`all:${c}`} className="text-[#F5A623] text-sm" data-testid={`inbox-line-category-${idx}-all-${c}`}>{CATEGORY_LABELS[c]} → all lines</SelectItem>)}
                  </SelectGroup>
                )}
              </SelectContent>
            </Select>
            <div className="relative min-w-0">
              <Input value={l.description || ''} onChange={e => update(l.id, { description: e.target.value })} disabled={readOnly}
                className={`${cell} ${l.suggested ? 'pr-6' : ''}`} placeholder="What is it?" data-testid={`inbox-line-description-${idx}`} />
              {l.suggested && (l.source === 'memory'
                ? <History className="w-3.5 h-3.5 text-sky-300 absolute right-2 top-1/2 -translate-y-1/2" title="Filled from past invoices you approved for this supplier — edit to confirm" data-testid={`inbox-line-memory-${idx}`} />
                : <Sparkles className="w-3.5 h-3.5 text-amber-400 absolute right-2 top-1/2 -translate-y-1/2" title="Suggested by AI — edit to confirm" />)}
            </div>
            <Input type="number" step="0.01" inputMode="decimal" value={l.amount ?? ''} onChange={e => update(l.id, { amount: e.target.value })} disabled={readOnly}
              className={`${cell} text-right tabular-nums no-spinner ${toPence(l.amount) <= 0 ? 'border-amber-500/50' : ''}`} placeholder="0.00" data-testid={`inbox-line-amount-${idx}`} />
            <VatRateSelect value={l.vat_rate} onChange={v => update(l.id, { vat_rate: v, vat_source: null })} disabled={readOnly} memory={l.vat_source === 'memory'} testid={`inbox-line-vat-rate-${idx}`} />
            {!readOnly ? (
              <button type="button" onClick={() => remove(l.id)} className="p-1 text-gray-500 hover:text-red-400" title="Remove line" data-testid={`inbox-line-remove-${idx}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            ) : <span />}
          </div>
        ))}
        <div className={`flex items-center justify-between gap-2 px-3 py-2 text-xs border-t border-white/10 ${balanced ? 'bg-emerald-500/10 text-emerald-300' : lines.length ? 'bg-amber-500/10 text-amber-200' : 'text-gray-400'}`} data-testid="inbox-lines-totals">
          <span>Lines <b className="tabular-nums">{fmtGBP(linesTotal)}</b> · Net <b className="tabular-nums">{fmtGBP(invoiceTotalPence || 0)}</b>{balanced && vatPence > 0 && <span className="text-emerald-300/70"> · + VAT {fmtGBP(vatPence)} → {fmtGBP(grossPence)} inc</span>}</span>
          <span className="tabular-nums flex items-center gap-2 text-right" data-testid="inbox-lines-diff">
            {balanced ? 'Balanced ✓' : diff === 0 && !invoiceTotalPence ? 'Enter the net (ex VAT) total' : linesLookGross ? `Lines add up to the gross — they should be ex VAT` : `${diff > 0 ? 'Unallocated' : 'Over by'} ${fmtGBP(Math.abs(diff))}`}
            {canSpread && (
              <button type="button" onClick={spread} className="underline text-[#F5A623] whitespace-nowrap" title={linesLookGross ? 'Scale every line down to the net so the VAT comes out pro-rata' : 'Scale every line so they add up to the net total'} data-testid="inbox-spread-btn">{linesLookGross ? 'Strip VAT pro-rata' : 'Spread pro-rata'}</button>
            )}
          </span>
        </div>
        {gapIsVat && !readOnly && (
          <div className="flex items-start gap-2 px-3 py-2 text-[11px] bg-red-500/10 text-red-200 border-t border-red-500/20" data-testid="inbox-gap-is-vat-warning">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span className="flex-1">The unallocated amount is exactly the VAT — the Net above is really the gross figure. Don't spread VAT into the ex-VAT lines; fix the Net instead.
              {aiDiffers && onUseAiTotals && <button type="button" onClick={onUseAiTotals} className="underline text-[#F5A623] ml-1.5" data-testid="inbox-gap-use-ai">Use the AI totals</button>}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
